"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Plus, ChevronDown } from "lucide-react"

import { useCurrentUser, useSignOut } from "@/features/auth/hooks"
import { canCreateInvoice, hasRole } from "@/features/auth/role"

interface AdminLayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: "Home", href: "/" },
  { name: "Invoices", href: "/invoices" },
  { name: "Catalogue", href: "/catalogue" },
  { name: "Analytics", href: "/analytics" },
]

function computeInitials(name: string, email: string): string {
  const source = name.trim() ? name : email
  const parts = source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
  if (parts.length === 0) {
    return "?"
  }
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("")
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const pathname = usePathname()
  const { data: user } = useCurrentUser()
  const signOut = useSignOut()

  const displayName = user?.display_name ?? ""
  const email = user?.email ?? ""
  const initials = computeInitials(displayName, email)
  const showNewInvoice = canCreateInvoice(user)
  const showNoRoleBanner = Boolean(user) && !hasRole(user)

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="flex h-14 items-center justify-between px-6">
          {/* Left side - Logo and Nav */}
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-2">
              <span className="text-h3 text-foreground">Billdozer</span>
            </Link>

            <nav className="flex items-center gap-1">
              {navigation.map((item) => {
                const isActive =
                  pathname === item.href ||
                  (item.href !== "/" && pathname?.startsWith(item.href))

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={cn(
                      "px-3 py-2 text-body rounded-md transition-colors",
                      isActive
                        ? "text-foreground bg-muted"
                        : "text-text-secondary hover:text-foreground hover:bg-muted",
                    )}
                  >
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Right side - Actions and User */}
          <div className="flex items-center gap-3">
            {showNewInvoice && (
              <Button asChild size="sm">
                <Link href="/invoices/new">
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Link>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-body hidden sm:inline">
                    {displayName || email}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-body font-medium">
                    {displayName || email}
                  </p>
                  <p className="text-caption text-muted-foreground">{email}</p>
                </div>
                <DropdownMenuItem
                  className="text-destructive"
                  disabled={signOut.isPending}
                  onSelect={(event) => {
                    event.preventDefault()
                    signOut.mutate()
                  }}
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {showNoRoleBanner && (
        <div
          role="alert"
          className="border-b border-destructive/50 bg-destructive/10 px-6 py-2 text-body text-destructive"
        >
          Your account has no role assigned. Contact an administrator to
          regain access.
        </div>
      )}

      {/* Main content */}
      <main className="flex-1">{children}</main>
    </div>
  )
}
